import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { MarketingShell, M } from "@/components/MarketingShell";
import { ShareBar } from "@/components/ShareBar";
import { getFoundingPlaces, FOUNDING_PLACES } from "@/lib/pricing.functions";

const DESC =
  "Claim a free Tiger's Eye review: honest, section-by-section critique of your own draft. Free for the first 49 students.";

export const Route = createFileRoute("/free-review")({
  head: () => ({
    meta: [
      { title: "Your free review — Tiger's Eye" },
      { name: "description", content: DESC },
      { property: "og:title", content: "Your free review — Tiger's Eye" },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://tigerseye.app/free-review" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://tigerseye.app/free-review" }],
  }),
  component: FreeReviewPage,
});

const STEPS = [
  { n: "01", t: "Create your free account", d: "Sign up with Google or email in under a minute." },
  { n: "02", t: "Upload your draft", d: "PDF, Word or plain text — or paste it in. Add your brief and level." },
  { n: "03", t: "Get your critique", d: "Clear comments on argument, structure, evidence and referencing. You make the changes." },
];

function FreeReviewPage() {
  const { data } = useQuery({
    queryKey: ["founding-places"],
    queryFn: () => getFoundingPlaces(),
  });
  const claimed = typeof data === "number" ? data : (data as { claimed?: number } | undefined)?.claimed;
  const pct = claimed != null ? Math.min(100, (claimed / FOUNDING_PLACES) * 100) : 0;

  return (
    <MarketingShell>
      <p className="text-xs uppercase tracking-[0.2em]" style={{ color: M.BRASS }}>Free review offer</p>
      <h1 className="mt-3 text-4xl leading-tight" style={{ fontFamily: M.SERIF }}>
        Your next draft, critiqued — free.
      </h1>
      <p className="mt-4" style={{ color: M.INK_SOFT }}>
        The first {FOUNDING_PLACES} students get Tiger&apos;s Eye free. After that it&apos;s just
        £4.95 per term. Tiger&apos;s Eye never writes or rewrites your work — it shows you what to
        improve, so your grade stays yours.
      </p>

      {claimed != null && (
        <div className="mt-8">
          <div className="h-2 w-full overflow-hidden rounded-full" style={{ background: M.RULE }}>
            <div className="h-full" style={{ width: `${pct}%`, background: M.BRASS }} />
          </div>
          <p className="mt-2 text-sm" style={{ color: M.INK_SOFT }}>
            {claimed} of {FOUNDING_PLACES} founding places claimed
          </p>
        </div>
      )}

      <ol className="mt-10 space-y-6">
        {STEPS.map((s) => (
          <li key={s.n} className="flex gap-4">
            <span className="text-sm font-semibold" style={{ color: M.BRASS }}>{s.n}</span>
            <div>
              <h2 className="text-lg" style={{ fontFamily: M.SERIF, color: M.INK }}>{s.t}</h2>
              <p className="text-sm" style={{ color: M.INK_SOFT }}>{s.d}</p>
            </div>
          </li>
        ))}
      </ol>

      <div className="mt-10 flex flex-wrap gap-3">
        <Link
          to="/signup"
          className="rounded-md px-5 py-3 text-sm font-medium"
          style={{ background: M.INK, color: M.BG }}
        >
          Claim my free place
        </Link>
        <Link
          to="/redeem"
          className="rounded-md border px-5 py-3 text-sm font-medium"
          style={{ borderColor: M.RULE, color: M.INK }}
        >
          I have a code
        </Link>
      </div>
      <ShareBar title="Free essay critique for the first 49 students — Tiger's Eye" url="https://tigerseye.app/free-review" />
      <p className="mt-6 text-sm" style={{ color: M.INK_FAINT }}>
        Questions? <Link to="/faq" className="underline">Read the FAQ</Link> or{" "}
        <Link to="/blog" className="underline">browse the blog</Link>.
      </p>
    </MarketingShell>
  );
}
